import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Component, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ViewContainerRef } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { SidePanelStateService } from '@services';
import { UserDataService } from '@services';

import { LogoutComponent } from './components/logout/logout.component';
import { HealthcheckService } from './services/healthcheck/healthcheck.service';
import { SidebarService } from './services/sidebar/sidebar.service';
import { StyleUtilService } from './services/style-util/style-util.service';
import { UserSessionMonitoringService } from './services/user-session-monitoring/user-session-monitoring.service';

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  private healthcheckService: HealthcheckService = inject(HealthcheckService);
  private styleUtilService: StyleUtilService = inject(StyleUtilService);
  private sidebarService: SidebarService = inject(SidebarService);
  private sidePanelStateService: SidePanelStateService = inject(SidePanelStateService);
  private router: Router = inject(Router);
  private userSessionMonitoringService: UserSessionMonitoringService = inject(
    UserSessionMonitoringService
  );
  private ngZone = inject(NgZone);
  private sidebarCollapseObserver: MutationObserver | null = null;

  sidebarCollapsed = false;
  activeRoute = '';
  isExpanded = true;
  showTitle = true;
  sidePanelExpanded = false;
  isRouterReady = false;
  showAppContent = false;
  private overlay = inject(Overlay);
  private vcr = inject(ViewContainerRef);
  private userDataService = inject(UserDataService);
  private logoutOverlayRef: OverlayRef | null = null;
  private logoutAnchorEl: HTMLElement | null = null;
  private onDocClickRef: ((evt: MouseEvent) => void) | null = null;

  sidebarItems = [
    {
      id: '0',
      label: 'Home',
      icon: '/assets/icons/home-mono.svg',
      route: '/home',
      locked: true
    },
    {
      id: '1',
      label: 'My portfolio',
      icon: '/assets/icons/my-portfolio-mono.svg',
      route: '/portfolio',
      locked: true
    },
    {
      id: '2',
      label: 'Preclarus Labs',
      icon: '/assets/icons/labs-preclarus-mono.svg',
      route: 'https://preclaruslabdata.ppdi.com/Preclarus/login',
      locked: true
    },
    {
      id: '3',
      label: 'MySupply',
      icon: '/assets/icons/my-supply-mono.svg',
      route: 'https://mysupply.patheon.com/login',
      locked: true
    },
    {
      id: '4',
      label: 'Preclarus Decision Console',
      icon: '/assets/icons/preclarus-decision-console-mono.svg',
      route: 'https://preclarusdecisionconsole.ppdi.com/home',
      locked: true
    }
  ];

  private toggleLogoutPopup(anchorEl: HTMLElement): void {
    if (this.logoutOverlayRef) {
      this.closeLogoutPopup();
      return;
    }
    this.logoutAnchorEl = anchorEl;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(anchorEl)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
          offsetX: 0,
          offsetY: 8
        }
      ]);

    this.logoutOverlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      hasBackdrop: false,
      panelClass: ['logout-overlay-panel', 'arrow-left']
    });

    const portal = new ComponentPortal(LogoutComponent, this.vcr);
    const ref = this.logoutOverlayRef.attach(portal);

    ref.instance.email = this.userDataService.getUsername() ?? '';
    this.styleUtilService.setProfileActiveOnEl(anchorEl, true);
    setTimeout(() => {
      this.onDocClickRef = (evt: MouseEvent) => {
        const overlayEl = this.logoutOverlayRef?.overlayElement;
        if (!overlayEl) return;
        const path = evt.composedPath ? evt.composedPath() : [];
        const anchor = this.logoutAnchorEl;
        if (anchor && path.includes(anchor)) return;
        const target = evt.target as Node | null;
        if (target && overlayEl.contains(target)) return;
        this.closeLogoutPopup();
      };
      document.addEventListener('click', this.onDocClickRef);
    }, 0);
  }

  private closeLogoutPopup(): void {
    if (this.logoutAnchorEl) {
      this.styleUtilService.setProfileActiveOnEl(this.logoutAnchorEl, false);
      this.logoutAnchorEl.blur?.();
    }

    if (this.onDocClickRef) {
      document.removeEventListener('click', this.onDocClickRef);
      this.onDocClickRef = null;
    }

    this.logoutOverlayRef?.dispose();
    this.logoutOverlayRef = null;
    this.logoutAnchorEl = null;
  }

  ngOnInit(): void {
    this.sidebarService.sidebarCollapsed$.subscribe((collapsed) => {
      this.sidebarCollapsed = collapsed;
    });

    this.sidePanelStateService.sidePanelExpanded$.subscribe((expanded) => {
      this.sidePanelExpanded = expanded;
    });

    this.userSessionMonitoringService.startPeriodicMonitoring();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Hide loading wheel after first successful navigation (guards passed)
        const wasNotReady = !this.isRouterReady;
        this.isRouterReady = true;
        this.showAppContent = true;

        // Initialize sidebar after first navigation completes
        if (wasNotReady) {
          setTimeout(() => {
            this.initializeSidebar();
          }, 0);
        }

        this.activeRoute = event.urlAfterRedirects || event.url;
        if (event.url === '/') {
          //redirect to home page
          this.router.navigate(['/home']);
        } else {
          // Update shadow DOM active state when route changes
          setTimeout(() => {
            this.updateShadowDOMActiveState();
          }, 0);
        }
      } else if (event instanceof NavigationError) {
        // Show router ready even on errors so user sees error page
        this.isRouterReady = true;
      } else if (event instanceof NavigationCancel) {
        // Guards failed - hide loading wheel but don't show app content
        this.isRouterReady = true;
        this.showAppContent = false;
      }
    });
  }

  private initializeSidebar(): void {
    this.styleUtilService.modifyKwaHeader();
    this.styleUtilService.bindKwaHeaderProfileClick((anchorEl) => {
      this.toggleLogoutPopup(anchorEl);
    });
    const kwaSidebarElement = document.querySelector('kwa-platform-nav') as HTMLElement;
    const shadowRoot = kwaSidebarElement?.shadowRoot;

    if (shadowRoot) {
      const setupSidebar = (): boolean => {
        const platformNavDiv = shadowRoot.querySelector('.platform-nav') as HTMLElement;

        if (platformNavDiv) {
          this.sidebarCollapseObserver?.disconnect();
          this.styleUtilService.removeSidebarOverflow(kwaSidebarElement);

          this.sidebarCollapseObserver = this.styleUtilService.observeSidebarCollapse(
            platformNavDiv,
            (collapsed: boolean) => {
              this.ngZone.run(() => {
                this.sidebarService.setSidebarCollapsed(collapsed);
                this.sidebarCollapsed = collapsed;
                // Re-bind click handlers when collapse state changes
                setTimeout(() => {
                  this.handleNavItemClick(shadowRoot);
                }, 100);
              });
            }
          );

          this.handleNavItemClick(shadowRoot);

          const itemElements = shadowRoot.querySelectorAll('.nav-item');
          itemElements.forEach((el, index) => {
            const item = this.sidebarItems[index];
            // External link indicator
            if (item && item.route && item.route.startsWith('http')) {
              const span = el.querySelector('.item-label span');
              if (span) {
                span.classList.add('external-link');
              }
            }
            // Active state
            // Only apply active state to internal routes (not external links)
            if (
              item &&
              typeof item.route === 'string' &&
              !item.route.startsWith('https://') &&
              item.route === this.activeRoute
            ) {
              el.classList.add('active');
            } else {
              el.classList.remove('active');
            }
          });

          if (!shadowRoot.querySelector('style[data-external-link]')) {
            const style = document.createElement('style');
            style.setAttribute('data-external-link', 'true');
            style.textContent = `
                span.external-link::after {
                  content: '';
                  display: inline-block;
                  vertical-align: middle;
                  margin-left: 10px;
                  width: 10px;
                  height: 10px;
                  background-image: url('/assets/icons/link.svg');
                  background-size: contain;
                  background-repeat: no-repeat;
                }
              `;
            shadowRoot.appendChild(style);
          }

          if (!shadowRoot.querySelector('style[data-active-indicator]')) {
            const style = document.createElement('style');
            style.setAttribute('data-active-indicator', 'true');
            style.textContent = `
                .nav-item.active {
                  border-left: 3px solid #D0021B;
                  background: #fff;
                }
              `;
            shadowRoot.appendChild(style);
          }
          return true;
        }
        return false;
      };

      // Try running immediately
      if (setupSidebar()) {
        return;
      }

      const observer = new MutationObserver(() => {
        if (setupSidebar()) {
          observer.disconnect();
        }
      });
      observer.observe(shadowRoot, { childList: true });
    }
  }

  private handleNavItemClick(shadowRoot: ShadowRoot): void {
    const itemElements = shadowRoot.querySelectorAll('.nav-item:not(.collapse-option)');
    itemElements.forEach((el, index) => {
      if (el.hasAttribute('data-click-bound')) {
        return;
      }
      el.setAttribute('data-click-bound', 'true');
      el.addEventListener('click', () => {
        const clickedItem = this.sidebarItems[index];
        if (clickedItem) {
          if (typeof clickedItem.route === 'string' && clickedItem.route.startsWith('https://')) {
            window.open(clickedItem.route, '_blank');
          } else {
            this.router.navigate([clickedItem.route]).then(() => {
              this.activeRoute = clickedItem.route;
              this.updateActiveNavItem(shadowRoot);
            });
          }
        }
      });
    });
  }

  private updateActiveNavItem(shadowRoot: ShadowRoot): void {
    const itemElements = shadowRoot.querySelectorAll('.nav-item');
    itemElements.forEach((el, index) => {
      const item = this.sidebarItems[index];
      if (item && !item.route.startsWith('https://') && item.route === this.activeRoute) {
        el.classList.add('active');
      } else {
        el.classList.remove('active');
      }
    });
  }

  private updateShadowDOMActiveState(): void {
    const kwaSidebarElement = document.querySelector('kwa-platform-nav') as HTMLElement;
    if (kwaSidebarElement?.shadowRoot) {
      this.updateActiveNavItem(kwaSidebarElement.shadowRoot);
    }
  }

  ngOnDestroy(): void {
    this.userSessionMonitoringService.stopMonitoring();
    this.closeLogoutPopup();
    this.sidebarCollapseObserver?.disconnect();
  }
}
